import { useEffect } from 'react';
import { X, FileText, Cpu, Shield, Radio } from 'lucide-react';
import { cn } from '@/lib/utils';
import { restoreBodyInteractivity } from '@/components/ui/dialog';
import { GlobalInstructionsPanel } from './GlobalInstructionsPanel';
import { ProviderSettingsPanel } from './ProviderSettingsPanel';
import { WorkspaceSettingsPanel } from './WorkspaceSettingsPanel';
import { RelaysSettingsPanel } from './RelaysSettingsPanel';

export type SettingsTab = 'instructions' | 'providers' | 'privacy' | 'relays';

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  tab: SettingsTab;
  onTabChange: (tab: SettingsTab) => void;
}

const TABS: { id: SettingsTab; label: string; icon: typeof FileText; hint: string }[] = [
  {
    id: 'instructions',
    label: 'Instructions',
    icon: FileText,
    hint: 'Global prompt, session & Nostr in prompts',
  },
  {
    id: 'providers',
    label: 'Providers',
    icon: Cpu,
    hint: 'API keys & models',
  },
  {
    id: 'relays',
    label: 'Relays',
    icon: Radio,
    hint: 'Your relays, read/write & Blossom',
  },
  {
    id: 'privacy',
    label: 'Privacy',
    icon: Shield,
    hint: 'History, vault & backup',
  },
];

export function SettingsModal({ open, onClose, tab, onTabChange }: SettingsModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    // Soft scroll lock while settings are open (no pointer-events on body)
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      restoreBodyInteractivity();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/70 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
        className="bg-[#121212] border border-[#262626] rounded-2xl w-full max-w-3xl max-h-[min(90vh,740px)] flex flex-col shadow-2xl overflow-hidden od-enter"
      >
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-5 sm:px-6 py-4 border-b border-[#1c1c1c]">
          <div>
            <h2
              id="settings-title"
              className="text-base font-semibold text-[#e8e6e3] tracking-tight"
              style={{ letterSpacing: '-0.02em' }}
            >
              Settings
            </h2>
            <p className="text-[12px] text-[#5a5a5a] mt-0.5">
              {TABS.find((t) => t.id === tab)?.hint}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors"
            aria-label="Close settings"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body: side nav + content */}
        <div className="flex-1 min-h-0 flex flex-col sm:flex-row">
          {/* Tab rail */}
          <nav className="shrink-0 sm:w-[11.5rem] border-b sm:border-b-0 sm:border-r border-[#1c1c1c] p-2 sm:p-3 flex sm:flex-col gap-0.5 overflow-x-auto scrollbar-none">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onTabChange(t.id)}
                  className={cn(
                    'flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-colors whitespace-nowrap',
                    active
                      ? 'bg-[#1f1f1f] text-[#e8e6e3] shadow-sm'
                      : 'text-[#5a5a5a] hover:text-[#c8c6c3] hover:bg-[#1a1a1a]/60',
                  )}
                >
                  <Icon
                    className={cn(
                      'w-4 h-4 shrink-0',
                      active ? 'text-[#e8e6e3]' : 'text-[#3a3a3a]',
                    )}
                  />
                  <span className="text-[13px] font-medium">{t.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Panel */}
          <div className="flex-1 min-h-0 overflow-y-auto p-5 sm:p-6 scrollbar-thin">
            {tab === 'instructions' && <GlobalInstructionsPanel />}
            {tab === 'providers' && (
              <ProviderSettingsPanel active={open && tab === 'providers'} />
            )}
            {tab === 'relays' && <RelaysSettingsPanel />}
            {tab === 'privacy' && <WorkspaceSettingsPanel />}
          </div>
        </div>
      </div>
    </div>
  );
}
